/**
 * QUIZ SCREEN — Certification Exam
 *
 * All quizzes: 5 questions, 80% pass (4/5 correct)
 *
 * Equipment courses (Peloton):
 * 1. Select equipment type
 * 2. Show equipment-specific instructions + video
 * 3. 5 equipment-specific quiz questions
 *
 * Other courses: 5 questions directly from course data.
 *
 * After passing driver cert:
 *   - If came from a locked job (has returnToCourse param), redirect to that course
 *   - Otherwise go back to training page
 */
import React, { useEffect, useState, useMemo } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, Alert, Platform, Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, fonts, RD } from '../../theme/colors';
import { useSelector, useDispatch } from 'react-redux';
import { api } from '../../services/api';
import { RootState, setCertifications, recordQuizFailure, clearQuizFailures, setAccountOnHold } from '../../store';
import { Toast } from '../../components/Toast';
import {
  MOCK_COURSES,
  EQUIPMENT_OPTIONS,
  EQUIPMENT_INSTRUCTIONS,
  EQUIPMENT_QUIZ_QUESTIONS,
  EquipmentType,
} from '../../data/mockData';

export function QuizScreen({ route, navigation }: any) {
  const { courseId, title, returnToCourse, fromJob, fromJobId, categorySlug } = route.params;
  const dispatch = useDispatch();
  const currentCerts = useSelector((s: RootState) => s.auth.certifications);
  const accountOnHold = useSelector((s: RootState) => s.auth.accountOnHold);
  const quizFailures = useSelector((s: RootState) => s.auth.quizFailures);
  const [course, setCourse] = useState<any>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<EquipmentType | null>(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const PELOTON_SLUGS = ['peloton-bike', 'peloton-bike-plus', 'peloton-tread', 'peloton-tread-plus', 'peloton-row', 'fitness-equipment'];
  const isEquipmentCourse = PELOTON_SLUGS.includes(course?.categorySlug);
  const isDriverCert = course?.categorySlug === 'driver-certification';
  // Stable key for the consecutive-failure counter (categorySlug is consistent across the
  // training list + cert banner; the route courseId is not).
  const failKey = course?.categorySlug || courseId;

  // Check if hold has been lifted by admin (auto-clear stale local hold)
  useEffect(() => {
    if (!accountOnHold) return;
    api.get('/training/hold-status')
      .then(r => {
        if (r.data && !r.data.onHold) {
          dispatch(setAccountOnHold(false));
          dispatch(clearQuizFailures(failKey));
        }
      })
      .catch(() => {});
  }, [accountOnHold]);

  useEffect(() => {
    // Always use mock course data as the base (has quiz questions, content, video URLs).
    // The training list OVERLAYS the backend DB id onto each course, so courseId is often
    // an API id that won't match a mock id — fall back to matching by categorySlug so the
    // quiz never loads blank. API course data has 0 questions (stored in a separate table).
    const mock = MOCK_COURSES.find(c => c.id === courseId)
      || (categorySlug ? MOCK_COURSES.find(c => c.categorySlug === categorySlug) : undefined);
    if (mock) {
      setCourse(mock);
    } else {
      // Fallback to API for courses not in mocks (e.g. auto-generated categories).
      // Back-fill quiz content from the matching mock by slug since the API has 0 questions.
      api.get(`/training/${courseId}`)
        .then(r => {
          const apiCourse = r.data || {};
          const mockBySlug = MOCK_COURSES.find(c => c.categorySlug === apiCourse.categorySlug);
          setCourse(mockBySlug
            ? { ...apiCourse, quizQuestions: mockBySlug.quizQuestions, textContent: mockBySlug.textContent }
            : apiCourse);
        })
        .catch(() => {});
    }
  }, [courseId, categorySlug]);

  // Auto-select equipment if course has a defaultEquipment (individual Peloton courses)
  useEffect(() => {
    if (course?.defaultEquipment && !selectedEquipment) {
      setSelectedEquipment(course.defaultEquipment as EquipmentType);
    }
  }, [course]);

  const questions = useMemo(() => {
    if (!course) return [];
    if (isEquipmentCourse && selectedEquipment) {
      return EQUIPMENT_QUIZ_QUESTIONS[selectedEquipment] || [];
    }
    return course.quizQuestions || [];
  }, [course, isEquipmentCourse, selectedEquipment]);

  const answer = (qId: string, idx: number) => {
    if (!submitted) setAnswers(p => ({ ...p, [qId]: idx }));
  };

  const submit = async () => {
    // A 0-question / misconfigured quiz must never be "submitted" — scoring it would yield a
    // spurious failure toward the 3-strike account hold.
    if (questions.length === 0) {
      Alert.alert('Quiz unavailable', 'This course has no questions yet. Please contact support.');
      return;
    }
    if (Object.keys(answers).length < questions.length) {
      Alert.alert('Incomplete', 'Please answer all questions before submitting.');
      return;
    }
    setSubmitting(true);

    let correct = 0;
    questions.forEach((q: any) => {
      if (answers[q.id] === q.correctIndex) correct++;
    });
    const score = Math.round((correct / questions.length) * 100);
    const passed = score >= 80;
    setResult({ score, passed });

    // Submit quiz to backend and wait for certification to be saved
    try {
      await api.post(`/training/${courseId}/submit-quiz`, {
        answers: Object.entries(answers).map(([questionId, selectedIndex]) => ({ questionId, selectedIndex })),
        equipmentType: selectedEquipment,
        categorySlug: course?.categorySlug,
      });
    } catch (err: any) {
      console.warn('[Quiz] Backend submit failed:', err?.response?.data || err?.message);
    }

    if (passed) {
      if (course?.categorySlug && !currentCerts.includes(course.categorySlug)) {
        dispatch(setCertifications([...currentCerts, course.categorySlug]));
      }
      dispatch(clearQuizFailures(failKey));
    } else {
      // Only a genuine failed attempt counts (we already bailed on 0-question quizzes).
      dispatch(recordQuizFailure(failKey));
    }

    setSubmitted(true);
    setSubmitting(false);
    if (passed) setShowToast(true);
  };

  const handleDone = () => {
    // If this was driver cert and there's a returnToCourse, chain to category quiz
    if (isDriverCert && returnToCourse) {
      const targetCourse = MOCK_COURSES.find(c => c.id === returnToCourse);
      navigation.replace('Quiz', {
        courseId: returnToCourse,
        title: targetCourse?.title || 'Training',
        fromJob,
        fromJobId,
      });
      return;
    }
    // If came from a locked job, redirect to Jobs tab and re-open the job detail
    if (fromJobId && fromJob) {
      navigation.navigate('Main', { screen: 'Jobs' });
      // Small delay to let tab mount, then open job detail
      setTimeout(() => {
        navigation.navigate('JobDetail', { jobId: fromJobId, job: fromJob });
      }, 100);
      return;
    }
    if (fromJobId) {
      navigation.navigate('Main', { screen: 'Jobs' });
      return;
    }
    // Navigate back to the Training tab
    navigation.navigate('Main', { screen: 'Training' });
  };

  if (!course) return <View style={styles.loadingWrap}><ActivityIndicator size="large" color={RD.ink} /></View>;

  // Account on hold — blocked from all quizzes
  if (accountOnHold) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
            <Ionicons name="chevron-back" size={24} color={RD.text} />
          </TouchableOpacity>
          <View style={styles.headerCenter}>
            <Text style={styles.headerTitle}>Account On Hold</Text>
          </View>
          <View style={{ width: 22 }} />
        </View>
        <View style={styles.holdWrap}>
          <View style={styles.holdIconWrap}>
            <Ionicons name="hand-left" size={48} color={RD.danger} />
          </View>
          <Text style={styles.holdTitle}>Account On Hold</Text>
          <Text style={styles.holdDesc}>
            Your account has been temporarily suspended due to multiple failed quiz attempts.
            An admin will review your account and approve it to continue.
          </Text>
          <Text style={styles.holdSub}>
            Please contact support if you have questions.
          </Text>
          <TouchableOpacity
            style={styles.holdBtn}
            onPress={() => navigation.goBack()}
            activeOpacity={0.8}
          >
            <Text style={styles.holdBtnText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Step 1: Equipment selection (for fitness-equipment courses)
  if (isEquipmentCourse && !selectedEquipment) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
            <Ionicons name="chevron-back" size={24} color={RD.text} />
          </TouchableOpacity>
          <View style={styles.headerCenter}>
            <Text style={styles.headerTitle} numberOfLines={1}>{title}</Text>
            <Text style={styles.headerSub}>Step 1: Select Equipment</Text>
          </View>
          <View style={{ width: 22 }} />
        </View>

        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.stepCard}>
            <View style={styles.stepIconWrap}>
              <Ionicons name="fitness-outline" size={32} color={RD.maroon} />
            </View>
            <Text style={styles.stepTitle}>Which equipment will you deliver?</Text>
            <Text style={styles.stepDesc}>
              Select the Peloton product type to see equipment-specific instructions and take the certification quiz.
            </Text>
          </View>

          {EQUIPMENT_OPTIONS.map(opt => (
            <TouchableOpacity
              key={opt.value}
              style={styles.equipmentOption}
              onPress={() => setSelectedEquipment(opt.value as EquipmentType)}
              activeOpacity={0.7}
            >
              <View style={styles.equipmentIconWrap}>
                <Ionicons name="bicycle-outline" size={20} color={RD.maroon} />
              </View>
              <Text style={styles.equipmentLabel}>{opt.label}</Text>
              <Ionicons name="chevron-forward" size={18} color={RD.g30} />
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  }

  // Step 2: Instructions (shown before quiz for equipment courses)
  if (isEquipmentCourse && showInstructions && selectedEquipment) {
    const instructions = EQUIPMENT_INSTRUCTIONS[selectedEquipment];
    const equipOption = EQUIPMENT_OPTIONS.find(o => o.value === selectedEquipment);
    const equipLabel = equipOption?.label;
    const videoUrl = equipOption?.videoUrl;

    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => course?.defaultEquipment ? navigation.goBack() : setSelectedEquipment(null)} style={styles.backBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
            <Ionicons name="chevron-back" size={24} color={RD.text} />
          </TouchableOpacity>
          <View style={styles.headerCenter}>
            <Text style={styles.headerTitle} numberOfLines={1}>{equipLabel}</Text>
            <Text style={styles.headerSub}>Step 2: Review Instructions</Text>
          </View>
          <View style={{ width: 22 }} />
        </View>

        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          {/* Video link */}
          {videoUrl && (
            <TouchableOpacity
              style={styles.videoCard}
              onPress={() => Linking.openURL(videoUrl)}
              activeOpacity={0.8}
            >
              <View style={styles.videoIconWrap}>
                <Ionicons name="play-circle" size={28} color={colors.white} />
              </View>
              <View style={styles.videoInfo}>
                <Text style={styles.videoTitle}>Watch Training Video</Text>
                <Text style={styles.videoSub}>{equipLabel} — YouTube</Text>
              </View>
              <Ionicons name="open-outline" size={18} color={RD.info} />
            </TouchableOpacity>
          )}

          <View style={styles.instructionCard}>
            <View style={styles.instructionHeader}>
              <Ionicons name="document-text-outline" size={20} color={RD.ink} />
              <Text style={styles.instructionTitle}>Delivery & Setup Instructions</Text>
            </View>
            <Text style={styles.instructionBody}>{instructions}</Text>
          </View>

          <View style={styles.quizReadyCard}>
            <Ionicons name="school-outline" size={28} color={RD.amber} />
            <Text style={styles.quizReadyTitle}>Ready for the quiz?</Text>
            <Text style={styles.quizReadyDesc}>
              You'll answer 5 questions about this equipment. You need 80% (4/5) to pass.
            </Text>
          </View>
        </ScrollView>

        <View style={styles.bottomBar}>
          <TouchableOpacity
            style={styles.submitBtn}
            onPress={() => setShowInstructions(false)}
            activeOpacity={0.8}
          >
            <Text style={styles.submitText}>Start Quiz</Text>
            <Ionicons name="arrow-forward" size={18} color={colors.white} />
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Step 3: The actual quiz
  const answeredCount = Object.keys(answers).length;
  const totalCount = questions.length;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => {
          if (isEquipmentCourse && !submitted) setShowInstructions(true);
          else navigation.goBack();
        }} style={styles.backBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
          <Ionicons name="chevron-back" size={24} color={RD.text} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle} numberOfLines={1}>
            {isEquipmentCourse
              ? EQUIPMENT_OPTIONS.find(o => o.value === selectedEquipment)?.label || title
              : title}
          </Text>
          <Text style={styles.headerSub}>{answeredCount}/{totalCount} answered</Text>
        </View>
        <View style={{ width: 22 }} />
      </View>

      <View style={styles.progressBar}>
        <View style={[styles.progressFill, { width: `${totalCount > 0 ? (answeredCount / totalCount) * 100 : 0}%` }]} />
      </View>

      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        {result && (
          <View style={[styles.resultCard, result.passed ? styles.resultPass : styles.resultFail]}>
            <View style={[styles.resultBadge, { backgroundColor: result.passed ? RD.money : RD.danger }]}>
              <Ionicons
                name={result.passed ? 'checkmark' : 'close'}
                size={32}
                color={colors.white}
              />
            </View>
            <Text style={[styles.resultTitle, { color: result.passed ? RD.money : RD.danger }]}>
              {result.passed ? 'You passed!' : 'Not quite'}
            </Text>
            <Text style={[styles.resultScore, { color: result.passed ? RD.money : RD.danger }]}>
              {result.score}%
            </Text>
            <Text style={styles.resultLabel}>
              {result.passed ? 'You are now certified for this category.' : 'You need 80% to pass.'}
            </Text>
          </View>
        )}

        {questions.map((q: any, qi: number) => (
          <View key={q.id}>
            <View style={styles.questionCard}>
              <View style={styles.questionHeader}>
                <View style={styles.questionNum}>
                  <Text style={styles.questionNumText}>{qi + 1}</Text>
                </View>
                <Text style={styles.questionMeta}>Question {qi + 1} of {totalCount}</Text>
              </View>
              <Text style={styles.questionText}>{q.questionText}</Text>
              <View style={styles.optionsList}>
                {q.options.map((opt: string, idx: number) => {
                  const isSelected = answers[q.id] === idx;
                  const showCorrect = submitted && q.correctIndex === idx;
                  const showWrong = submitted && isSelected && q.correctIndex !== idx;

                  return (
                    <TouchableOpacity
                      key={idx}
                      style={[
                        styles.option,
                        isSelected && styles.optionSelected,
                        showCorrect && styles.optionCorrect,
                        showWrong && styles.optionWrong,
                      ]}
                      onPress={() => answer(q.id, idx)}
                      disabled={submitted}
                      activeOpacity={0.7}
                    >
                      <View style={[
                        styles.optionDot,
                        isSelected && styles.optionDotSelected,
                        showCorrect && styles.optionDotCorrect,
                        showWrong && styles.optionDotWrong,
                      ]}>
                        {showCorrect && <Ionicons name="checkmark" size={12} color={colors.white} />}
                        {showWrong && <Ionicons name="close" size={12} color={colors.white} />}
                        {isSelected && !submitted && <View style={styles.optionDotInner} />}
                      </View>
                      <Text style={[
                        styles.optionText,
                        isSelected && styles.optionTextSelected,
                        showCorrect && styles.optionTextCorrect,
                        showWrong && styles.optionTextWrong,
                      ]}>{opt}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              {submitted && !!q.explanation && answers[q.id] !== q.correctIndex && (
                <Text style={styles.explanation}>{q.explanation}</Text>
              )}
            </View>
          </View>
        ))}
      </ScrollView>

      {!submitted && totalCount > 0 && (
        <View style={styles.bottomBar}>
          <TouchableOpacity
            style={[styles.submitBtn, answeredCount < totalCount && styles.btnDisabled]}
            onPress={submit}
            disabled={answeredCount < totalCount || submitting}
            activeOpacity={0.8}
          >
            {submitting ? (
              <ActivityIndicator color={colors.white} />
            ) : (
              <Text style={styles.submitText}>Submit Quiz</Text>
            )}
          </TouchableOpacity>
        </View>
      )}

      {submitted && (
        <View style={styles.bottomBar}>
          {!result?.passed ? (
            <>
              {(quizFailures[failKey] || 0) >= 2 && (quizFailures[failKey] || 0) < 3 && (
                <Text style={styles.failWarning}>
                  <Ionicons name="warning" size={13} color={RD.danger} /> Last attempt — your account will be put on hold if you fail again.
                </Text>
              )}
              <TouchableOpacity
                style={[styles.submitBtn, accountOnHold && styles.btnDisabled]}
                onPress={() => {
                  if (accountOnHold) return;
                  setSubmitted(false); setResult(null); setAnswers({});
                }}
                disabled={accountOnHold}
                activeOpacity={0.8}
              >
                <Ionicons name="refresh" size={18} color={colors.white} />
                <Text style={styles.submitText}>Try Again ({3 - (quizFailures[failKey] || 0)} {(3 - (quizFailures[failKey] || 0)) === 1 ? 'attempt' : 'attempts'} left)</Text>
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity
              style={[styles.submitBtn, { backgroundColor: RD.money }]}
              onPress={handleDone}
              activeOpacity={0.8}
            >
              <Ionicons name="checkmark-circle" size={18} color={colors.white} />
              <Text style={styles.submitText}>Done</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      <Toast
        visible={showToast}
        message="Certification Complete!"
        type="success"
        duration={3000}
        onDismiss={() => setShowToast(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container:         { flex: 1, backgroundColor: RD.bg },
  loadingWrap:       { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: RD.bg },

  // Header
  header:            { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.lg, paddingTop: Platform.OS === 'ios' ? 56 : 36, paddingBottom: spacing.md, backgroundColor: RD.surface, borderBottomWidth: 1, borderBottomColor: RD.border },
  backBtn:           { padding: 4, minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  headerCenter:      { flex: 1, alignItems: 'center' },
  headerTitle:       { fontSize: 19, fontWeight: '700', color: RD.text, fontFamily: fonts.semiBold },
  headerSub:         { fontSize: 12, color: RD.textFaint, marginTop: 2 },

  // Progress
  progressBar:       { height: 4, backgroundColor: RD.g07 },
  progressFill:      { height: 4, backgroundColor: RD.maroon, borderRadius: 2 },

  content:           { padding: spacing.lg, gap: 14, paddingBottom: 120 },

  // Equipment Selection Step
  stepCard:          { backgroundColor: RD.surface, borderRadius: 18, padding: spacing.xl, alignItems: 'center', gap: 12, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  stepIconWrap:      { width: 64, height: 64, borderRadius: 32, backgroundColor: RD.maroonSoft, justifyContent: 'center', alignItems: 'center' },
  stepTitle:         { fontSize: 20, fontWeight: '800', color: RD.text, textAlign: 'center', fontFamily: fonts.heavy },
  stepDesc:          { fontSize: 14, color: RD.textMuted, textAlign: 'center', lineHeight: 20 },

  equipmentOption:   { flexDirection: 'row', alignItems: 'center', gap: 14, backgroundColor: RD.surface, borderRadius: 18, padding: 16, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  equipmentIconWrap: { width: 44, height: 44, borderRadius: 22, backgroundColor: RD.maroonSoft, justifyContent: 'center', alignItems: 'center' },
  equipmentLabel:    { flex: 1, fontSize: 15, fontWeight: '600', color: RD.text },

  // Video card
  videoCard:         { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: RD.ink, borderRadius: 18, padding: 16, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  videoIconWrap:     { width: 44, height: 44, borderRadius: 22, backgroundColor: 'rgba(255,255,255,0.15)', justifyContent: 'center', alignItems: 'center' },
  videoInfo:         { flex: 1 },
  videoTitle:        { fontSize: 14, fontWeight: '700', color: '#fff', fontFamily: fonts.semiBold },
  videoSub:          { fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 2 },

  // Instructions Step
  instructionCard:   { backgroundColor: RD.surface, borderRadius: 18, padding: 16, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  instructionHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: spacing.md },
  instructionTitle:  { fontSize: 16, fontWeight: '700', color: RD.text, fontFamily: fonts.semiBold },
  instructionBody:   { fontSize: 14, color: RD.textSec, lineHeight: 22 },

  quizReadyCard:     { backgroundColor: RD.amberSoft, borderRadius: 18, padding: spacing.xl, alignItems: 'center', gap: 8, borderWidth: 1, borderColor: RD.amber + '40' },
  quizReadyTitle:    { fontSize: 18, fontWeight: '700', color: RD.text, fontFamily: fonts.semiBold },
  quizReadyDesc:     { fontSize: 14, color: RD.textSec, textAlign: 'center', lineHeight: 20 },

  // Result (v5: filled circle badge + title + score)
  resultCard:        { borderRadius: 18, padding: spacing.xl, alignItems: 'center', gap: 6, borderWidth: 1, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  resultPass:        { backgroundColor: RD.moneySoft, borderColor: RD.money + '30' },
  resultFail:        { backgroundColor: RD.dangerSoft, borderColor: RD.danger + '30' },
  resultBadge:       { width: 64, height: 64, borderRadius: 32, justifyContent: 'center', alignItems: 'center', marginBottom: 8 },
  resultTitle:       { fontSize: 22, fontWeight: '800', fontFamily: fonts.heavy },
  resultScore:       { fontSize: 40, fontWeight: '900', fontFamily: fonts.heavy },
  resultLabel:       { fontSize: 14, fontWeight: '500', color: RD.textMuted, textAlign: 'center' },

  // Questions (v5: number chip + meta, then large question text, then options)
  questionCard:      { backgroundColor: RD.surface, borderRadius: 18, padding: 16, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  questionHeader:    { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: spacing.sm },
  questionNum:       { width: 28, height: 28, borderRadius: 14, backgroundColor: RD.ink, justifyContent: 'center', alignItems: 'center' },
  questionNumText:   { fontSize: 13, fontWeight: '700', color: '#fff' },
  questionMeta:      { fontSize: 12, fontWeight: '600', color: RD.textFaint },
  questionText:      { fontSize: 16.5, fontWeight: '700', color: RD.text, lineHeight: 24, marginBottom: spacing.md },
  optionsList:       { gap: 8 },
  option:            { flexDirection: 'row', alignItems: 'center', padding: 15, borderRadius: 16, borderWidth: 1.5, borderColor: RD.g15, backgroundColor: RD.surface, gap: 12 },
  optionSelected:    { borderColor: RD.ink },
  optionCorrect:     { borderColor: RD.money, backgroundColor: RD.moneySoft },
  optionWrong:       { borderColor: RD.danger, backgroundColor: RD.dangerSoft },
  optionDot:         { width: 24, height: 24, borderRadius: 12, borderWidth: 1.5, borderColor: RD.g15, justifyContent: 'center', alignItems: 'center' },
  optionDotSelected: { borderColor: RD.ink },
  optionDotCorrect:  { borderColor: RD.money, backgroundColor: RD.money },
  optionDotWrong:    { borderColor: RD.danger, backgroundColor: RD.danger },
  optionDotInner:    { width: 10, height: 10, borderRadius: 5, backgroundColor: RD.ink },
  optionText:        { flex: 1, fontSize: 14, color: RD.textSec, lineHeight: 20 },
  optionTextSelected:{ color: RD.text, fontWeight: '600' },
  optionTextCorrect: { color: RD.money, fontWeight: '600' },
  optionTextWrong:   { color: RD.danger, fontWeight: '600' },
  explanation:       { marginTop: 10, fontSize: 13, lineHeight: 19, color: RD.textSec, backgroundColor: RD.g03, borderRadius: 12, padding: 12 },

  // Bottom
  bottomBar:         { position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: RD.surface, padding: spacing.lg, paddingBottom: Platform.OS === 'ios' ? 36 : 20, borderTopWidth: 1, borderTopColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: -2 }, elevation: 4 },
  submitBtn:         { backgroundColor: RD.ink, borderRadius: 16, height: 54, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 8 },
  submitText:        { color: '#fff', fontWeight: '600', fontSize: 16, fontFamily: fonts.semiBold },
  btnDisabled:       { opacity: 0.4 },
  failWarning:       { fontSize: 13, color: RD.danger, textAlign: 'center', marginBottom: 10, fontWeight: '600', lineHeight: 18 },

  // Account hold screen
  holdWrap:          { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },
  holdIconWrap:      { width: 96, height: 96, borderRadius: 48, backgroundColor: RD.dangerSoft, justifyContent: 'center', alignItems: 'center', marginBottom: spacing.lg },
  holdTitle:         { fontSize: 24, fontWeight: '800', color: RD.text, marginBottom: spacing.sm, fontFamily: fonts.heavy },
  holdDesc:          { fontSize: 15, color: RD.textSec, textAlign: 'center', lineHeight: 22, marginBottom: spacing.md, paddingHorizontal: spacing.lg },
  holdSub:           { fontSize: 13, color: RD.textMuted, textAlign: 'center', marginBottom: spacing.xl },
  holdBtn:           { backgroundColor: RD.ink, borderRadius: 16, height: 54, paddingHorizontal: 40, justifyContent: 'center', alignItems: 'center' },
  holdBtnText:       { color: '#fff', fontWeight: '700', fontSize: 16 },
});
